import { useSplitTestBranch } from '@api/lendingAPI/lapiHooks';
import { getAccountsQueryConfig } from '@api/lendingAPI/queryConfigs';
import { FormattedCurrency } from '@iwIntlProvider/FormattedCurrency';
import { Spinner } from '@iwoca/orion';
import { useQuery } from '@tanstack/react-query';
import { FormattedMessage } from 'react-intl';

import TopUpStatusMessage from './TopUpStatusMessage';
import { useStateKey } from '../../../../../iwQueryClient/useStateKey';
import FallbackErrorModal from '../../../../modals/fallbackErrorModal/FallbackErrorModal';
import { repeatsTopUpEligibilitySplitTestName } from '../../NewOutstandingView.helpers';

export const NotYetTopUpEligible = ({ accountId }: { accountId: string }) => {
    const stateKey = useStateKey();

    const {
        data: accountsResponse,
        isLoading: accountsLoading,
        isError: accountsError
    } = useQuery(getAccountsQueryConfig(stateKey));

    const { branch, isLoading: splitTestLoading } = useSplitTestBranch({
        test: repeatsTopUpEligibilitySplitTestName,
        suspense: false
    });

    if (accountsLoading || splitTestLoading) {
        return (
            <div className="flex justify-center">
                <Spinner size="large" />
            </div>
        );
    }

    if (accountsError) {
        return <FallbackErrorModal />;
    }

    const inFirstRepaymentTest = branch === 'INCLUDE';

    const account = accountsResponse.data!.accounts.find(
        (account) => account.account_id === accountId
    )!;
    const amountToRepayBeforeTopUp =
        account.topup_eligibility!.amount_to_repay_before_topup;

    return (
        <TopUpStatusMessage
            heading={
                inFirstRepaymentTest ? (
                    <FormattedMessage
                        id="_.outstanding_view.notYetTopupEligibleFirstRepaymentAlertText"
                        defaultMessage="You're almost there!"
                    />
                ) : (
                    <FormattedMessage
                        id="_.outstanding_view.notYetTopupEligibleAlertText"
                        defaultMessage="You can request more funds once you've repaid {amount} - "
                        values={{
                            amount: (
                                <FormattedCurrency
                                    value={amountToRepayBeforeTopUp}
                                />
                            )
                        }}
                    />
                )
            }
            description={
                inFirstRepaymentTest ? (
                    <FormattedMessage
                        id="_.outstanding_view.notYetTopupEligibleFirstRepaymentAlertDescription"
                        defaultMessage="You'll be eligible to request more funds as soon as your first scheduled repayment is processed (usually 30 days after your loan started)."
                    />
                ) : (
                    <FormattedMessage
                        id="_.outstanding_view.notYetTopupEligibleAlertDescription"
                        defaultMessage="You've passed our eligibility checks but your approval will still be subject to a credit review"
                    />
                )
            }
            ctaText={
                <FormattedMessage
                    id="_.outstanding_view.notTopupEligibleCTAText"
                    defaultMessage="Get in touch"
                />
            }
        />
    );
};
